<template>
    <form :id="id" novalidate @submit="submitForm">
        <template v-for="field in form.fields" v-if="!loading && !formNotFound" :key="field.id">
            <component :is="'Mf' + field.type" :field="field" :fieldCols="fieldCols"
                       :labelCols="labelCols" :value="field.value" @update:modelValue="setValue(field, $event)"></component>
        </template>

        <div v-show="form.spamProtected" class="form-group row">
            <div :id="recaptchaId" :class="'offset-' + labelCols + ' col-'  + fieldCols"></div>
        </div>
        <button v-if="!loading && !formNotFound" :disabled="saving" class="btn btn-primary" type="submit">
            <span v-if="!saving">
                {{ sendText }}
            </span>
            <div v-else class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </button>
        <div v-if="loading" class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
        <div v-if="formNotFound && !loginRequired" class="row">
            <div class="alert alert-warning col-12" role="alert">
                {{ formLoadErrorText }}
            </div>
        </div>
        <a v-if="formNotFound && loginRequired" :href="generateLoginUrl()" class="btn btn-primary"> {{ loginText }} </a>
    </form>
</template>

<script>
import Axios from 'axios';
import MfTextbox from './fieldTypes/Textbox.vue';
import MfTextarea from './fieldTypes/Textarea.vue';
import MfText from './fieldTypes/Text.vue';
import MfToggle from './fieldTypes/Toggle.vue';
import MfMultiselect from './fieldTypes/Multiselect.vue';
import MfSelect from './fieldTypes/Select.vue';
import MfDate from './fieldTypes/Date.vue';
import MfUpload from './fieldTypes/Upload.vue';

export default {
    components: {MfTextbox, MfTextarea, MfText, MfToggle, MfMultiselect, MfSelect, MfDate, MfUpload},
    props: {
        count: {
            type: Boolean,
            default: false
        },
        pageId: {
            type: String,
            required: true
        },
        websiteId: {
            type: String,
            required: true
        },
        formId: {
            type: String,
            required: true
        },
        labelCols: {
            type: String,
            required: true
        },
        fieldCols: {
            type: String,
            required: true
        },
        returnUrl: {
            type: String,
            default: ''
        },
        parentId: {
            type: String,
            default: ''
        },
        formLoadErrorText: {
            type: String,
            required: true,
        },
        sendText: {
            type: String,
            required: true
        },
        loginText: {
            type: String,
            required: true
        },
        language: {
            type: String,
            required: true
        },
        reCaptchaKey: {
            type: String,
            default: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'
        },
        loginUrl: {
            type: String
        },
        mode: {
            type: String,
            default: 'entry'
        }
    },
    data() {
        return {
            form: {},
            loginRequired: false,
            loading: true,
            saving: false,
            formNotFound: false,
            files: [],
            captcha: {}
        };
    },
    computed: {
        id() {
            return Math.floor(Math.random() * 100).toString();
        },
        recaptchaId() {
            return 'recaptcha-' + this.id;
        }
    },
    beforeMount() {
        this.loadForm();
    },
    mounted() {
        this.loadCaptcha();
    },
    methods: {
        submitForm(e) {
            e.preventDefault();
            let captchaResponse = '';
            if (this.form.spamProtected) {
                captchaResponse = window.grecaptcha.getResponse(this.captcha);
            }
            if (this.validateForm() && (!this.form.spamProtected || captchaResponse !== '')) {
                this.saving = true;
                let formData = new FormData();
                for (let i = 0; i < this.files.length; i++) {
                    formData.append(this.files[i].key, this.files[i].file);
                }
                let data = {
                    count: this.count,
                    formId: this.formId,
                    pageId: this.pageId,
                    parentId: this.parentId,
                    websiteId: this.websiteId,
                    fields: this.form.fields,
                    captchaResponse: captchaResponse
                };
                formData.append("data", JSON.stringify(data));
                Axios.post(`/umbraco/mdforms/${this.mode}/create`, formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                })
                        .then(() => {
                            if (this.returnUrl !== '') {
                                window.location = this.returnUrl;
                            } else {
                                this.loadForm();
                                this.removeValidationInfo();
                                this.saving = false;
                                this.$emit('submitted');
                            }
                        });
            }
        },
        loadForm() {
            this.loading = true;
            Axios.get('/umbraco/mdforms/form/get?id=' + this.formId + '&language=' + this.language).then(response => {
                this.form = response.data;
                this.loading = false;
                if (response.data.alreadySubmitted) {
                    this.$emit("submitted");
                }
            }).catch(error => {
                if (error.response.status === 401) {
                    this.loginRequired = true;
                }
                this.formNotFound = true;
                this.loading = false;
            });
        },
        validateForm() {
            this.removeValidationInfo()
            let form = document.getElementById(this.id);
            let valid = form.checkValidity();
            if (!valid) {
                form.classList.add('was-validated');
            }
            return valid;
        },
        removeValidationInfo() {
            let form = document.getElementById(this.id);
            form.classList.remove('was-validated');
        },
        setValue(field, data) {
            field.value = data.value;
            if (data.files && data.files.length > 0) {
                let found = false;
                for (let file in this.files) {
                    if (file.key === field.formFieldId) {
                        found = true;
                        this.files.push({key: field.formFieldId, file: data.files[0]});
                    }
                }
                if (!found) {
                    this.files.push({key: field.formFieldId, file: data.files[0]});
                }
            }
        },
        loadCaptcha() {
            if (window.grecaptcha.render !== undefined) {
                let captchaElement = document.getElementById(this.recaptchaId);
                if (captchaElement !== null) {
                    this.captcha = window.grecaptcha.render(captchaElement, {
                        'sitekey': this.reCaptchaKey
                    });
                }
            } else {
                window.setTimeout(this.loadCaptcha, 500);
            }
        },
        generateLoginUrl() {
            return this.loginUrl + '?returnUrl=' + window.location.pathname;
        }
    }
}
</script>